import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { CartItem, CustomerItemPrice, PaymentEntry } from '@/types';
import { calcLineAmount, roundMoney } from '@/utils/currency';

export const MAX_HELD_CARTS = 5;

export interface DeliveryInfo {
  address: string;
  postal_code: string;
  recipient_name?: string;
  phone?: string;
  notes?: string;
  delivery_charge?: number;
}

export interface CartSnapshot {
  id: string;
  label: string;
  heldAt: string;
  items: CartItem[];
  customer: string;
  customerName: string;
  customerType: string;
  fulfillmentType?: 'Pickup' | 'Delivery';
  deliveryInfo?: DeliveryInfo;
  discount: number;
  tax: number;
  notes: string;
  payments: PaymentEntry[];
  customerMobile?: string;
  editingInvoiceDate?: string;
  customerCustomPrices?: CustomerItemPrice[];
}

export type HoldCartResult = { ok: true; id: string } | { ok: false; error: string };
export type ResumeHoldResult = { ok: true } | { ok: false; error: string };



interface CartStore {
  items: CartItem[];
  customer: string;
  customerName: string;
  customerType: string;
  fulfillmentType: 'Pickup' | 'Delivery';
  deliveryInfo: DeliveryInfo;
  discount: number;
  tax: number;
  notes: string;
  editingInvoiceDate?: string;
  payments: PaymentEntry[];
  holds: CartSnapshot[];
  customerCustomPrices?: CustomerItemPrice[];
  repeatItems: boolean;

  toggleRepeatItems: () => void;
  addItem: (item: CartItem) => void;
  removeItem: (index: number) => void;
  updateItemName: (index: number, newName: string) => void;
  updateLineComment: (index: number, comment: string) => void;
  updateGeneralItem: (index: number, newName: string, costPrice: number, salePrice: number, isService: boolean) => void;
  updateQuantity: (index: number, qty: number) => void;
  updateRate: (index: number, rate: number) => void;
  updateSerial: (index: number, serial: string, imei?: string, mac?: string) => void;
  setCustomer: (
    id: string,
    name: string,
    type?: string,
    customPrices?: CustomerItemPrice[],
    pricingMode?: 'product_price' | 'runtime_charge'
  ) => void;
  setFulfillmentType: (type: 'Pickup' | 'Delivery', pricingMode?: 'product_price' | 'runtime_charge') => void;
  setDeliveryInfo: (info: Partial<DeliveryInfo>) => void;
  setDiscount: (d: number) => void;
  setTax: (t: number) => void;
  setNotes: (n: string) => void;
  setEditingInvoiceDate: (d?: string) => void;
  addPayment: (payment: PaymentEntry) => void;
  removePayment: (index: number) => void;
  updatePayment: (index: number, payment: Partial<PaymentEntry>) => void;
  clearCart: () => void;

  holdCart: (opts?: { customerMobile?: string }) => HoldCartResult;
  resumeHold: (id: string, opts?: { customerMobile?: string }) => ResumeHoldResult;
  discardHold: (id: string) => void;

  getSubtotal: () => number;
  getItemTotal: () => number;
  getServiceTotal: () => number;
  getDiscountAmount: () => number;
  getTaxAmount: () => number;
  getNetTotal: () => number;
  getPaidAmount: () => number;
  getBalance: () => number;
  getTotalItems: () => number;
}

function snapshotFromState(
  s: {
    items: CartItem[];
    customer: string;
    customerName: string;
    customerType: string;
    fulfillmentType: 'Pickup' | 'Delivery';
    deliveryInfo: DeliveryInfo;
    discount: number;
    tax: number;
    notes: string;
    payments: PaymentEntry[];
    holds: CartSnapshot[];
    editingInvoiceDate?: string;
    customerCustomPrices?: CustomerItemPrice[];
  },
  customerMobile?: string,
): CartSnapshot {
  const id = `hold_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const label =
    s.customerName?.trim() ||
    (s.customer ? s.customer : `Hold #${s.holds.length + 1}`);
  return {
    id,
    label,
    heldAt: new Date().toISOString(),
    items: s.items.map((i) => ({ ...i })),
    customer: s.customer,
    customerName: s.customerName,
    customerType: s.customerType || 'Retailer',
    fulfillmentType: s.fulfillmentType || 'Pickup',
    deliveryInfo: s.deliveryInfo ? { ...s.deliveryInfo } : { address: '', postal_code: '', recipient_name: '', phone: '', notes: '', delivery_charge: 0 },
    discount: s.discount,
    tax: s.tax,
    notes: s.notes,
    payments: s.payments.map((p) => ({ ...p })),
    customerMobile: customerMobile || undefined,
    editingInvoiceDate: s.editingInvoiceDate,
    customerCustomPrices: s.customerCustomPrices ? [...s.customerCustomPrices] : [],
  };
}

export const findCustomerCustomPrice = (
  item: CartItem,
  customerPrices: CustomerItemPrice[] | undefined
): CustomerItemPrice | undefined => {
  if (!customerPrices || !customerPrices.length) return undefined;
  const itemVar = item.variant ? String(item.variant).trim() : '';
  return customerPrices.find((p) => {
    const itemMatches =
      p.item === item.item ||
      (item.item_code && p.item === item.item_code) ||
      (p.item_name && item.item_name && p.item_name.trim().toLowerCase() === item.item_name.trim().toLowerCase());
    if (!itemMatches) return false;
    const ruleVar = p.variant ? String(p.variant).trim() : '';
    return !ruleVar || ruleVar === itemVar;
  });
};

export const resolveItemRate = (
  item: CartItem,
  customerPrices: CustomerItemPrice[] | undefined,
  customerType: string,
  fulfillmentType: 'Pickup' | 'Delivery',
  pricingMode?: 'product_price' | 'runtime_charge'
): number => {
  // 1. Look for custom agreed customer price
  const cp = findCustomerCustomPrice(item, customerPrices);

  if (cp) {
    if (fulfillmentType === 'Delivery' && pricingMode === 'product_price') {
      if (cp.custom_delivery_price != null && cp.custom_delivery_price > 0) {
        return cp.custom_delivery_price;
      }
      if (item.delivery_price != null && item.delivery_price > 0) {
        return item.delivery_price;
      }
    }
    return cp.custom_price;
  }

  // 2. Fallback to standard / delivery / customer type prices
  const retailRate = item.retail_price ?? item.rate;
  if (fulfillmentType === 'Delivery' && pricingMode !== 'runtime_charge' && item.delivery_price != null && item.delivery_price > 0) {
    return item.delivery_price;
  }
  if (customerType === 'Wholesale') {
    if (item.wholesale_price != null && item.wholesale_price > 0) return item.wholesale_price;
    if (item.reseller_price != null && item.reseller_price > 0) return item.reseller_price;
  } else if (customerType === 'Distributor') {
    if (item.reseller_price != null && item.reseller_price > 0) return item.reseller_price;
  }
  return retailRate;
};

const emptyActive = {
  items: [] as CartItem[],
  customer: '',
  customerName: '',
  customerType: 'Retailer',
  customerCustomPrices: [] as CustomerItemPrice[],
  fulfillmentType: 'Pickup' as 'Pickup' | 'Delivery',
  deliveryInfo: {
    address: '',
    postal_code: '',
    recipient_name: '',
    phone: '',
    notes: '',
    delivery_charge: 0,
  },
  discount: 0,
  tax: 0,
  notes: '',
  editingInvoiceDate: undefined,
  payments: [] as PaymentEntry[],
};

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      ...emptyActive,
      holds: [],
      repeatItems: false,

      toggleRepeatItems: () => set((state) => ({ repeatItems: !state.repeatItems })),

      addItem: (item) => {
        const items = get().items;
        // Serial/IMEI lines must stay separate (one unit per IMEI)
        const isSerialLine = !!(item.imei || item.serial || item.track_serials);
        const existing = (isSerialLine || get().repeatItems)
          ? -1
          : items.findIndex(
              (i) =>
                i.item === item.item &&
                i.item_name === item.item_name &&
                i.variant === item.variant &&
                !i.imei &&
                !i.serial
            );
        if (existing >= 0) {
          const updated = [...items];
          updated[existing].quantity += item.quantity;
          updated[existing].amount = calcLineAmount(updated[existing].quantity, updated[existing].rate);
          if (item.customer_custom_price != null) {
            updated[existing].customer_custom_price = item.customer_custom_price;
          }
          if (item.customer_custom_price != null && item.customer_custom_price > 0) {
            updated[existing].min_rate = 0;
          } else if (item.min_rate != null) {
            updated[existing].min_rate = item.min_rate;
          }
          if (item.tax_rate != null) {
            updated[existing].tax_rate = item.tax_rate;
          }
          set({ items: updated });
        } else {
          set({
            items: [...items, {
              ...item,
              amount: calcLineAmount(item.quantity, item.rate),
            }],
          });
        }
      },

      removeItem: (index) => set({ items: get().items.filter((_, i) => i !== index) }),

      updateItemName: (index, newName) => {
        const items = [...get().items];
        items[index].item_name = newName.trim() || items[index].item_name;
        set({ items });
      },

      updateLineComment: (index, comment) => {
        const items = [...get().items];
        items[index].line_comment = comment.trim();
        set({ items });
      },

      updateGeneralItem: (index, newName, costPrice, salePrice, isService) => {
        const items = [...get().items];
        items[index].item_name = newName;
        items[index].cost_price = costPrice;
        items[index].rate = salePrice;
        items[index].amount = calcLineAmount(items[index].quantity, salePrice);
        items[index].item_type = isService ? 'Service' : 'Product';
        set({ items });
      },

      updateQuantity: (index, qty) => {
        if (qty < 0) return;
        const items = [...get().items];
        items[index].quantity = qty;
        items[index].amount = calcLineAmount(qty, items[index].rate);
        set({ items });
      },

      updateRate: (index, rate) => {
        set((state) => {
          const items = [...state.items];
          items[index].rate = rate;
          items[index].amount = rate * items[index].quantity;
          return { items };
        });
      },

      updateSerial: (index, serial, imei, mac) => {
        set((state) => {
          const items = [...state.items];
          items[index].serial = serial;
          if (imei) items[index].imei = imei;
          if (mac) items[index].mac_address = mac;
          return { items };
        });
      },

      setCustomer: (id, name, type = 'Customer', customPrices, pricingMode) => {
        const prev = get();
        const nextPrices = customPrices !== undefined ? customPrices : (id === prev.customer ? prev.customerCustomPrices : []);
        const pricesChanged = JSON.stringify(prev.customerCustomPrices || []) !== JSON.stringify(nextPrices || []);

        if (
          prev.customer === id &&
          prev.customerName === name &&
          prev.customerType === type &&
          !pricesChanged
        ) {
          return;
        }

        const items = prev.items.map((item) => {
          const newRate = resolveItemRate(item, nextPrices, type, prev.fulfillmentType, pricingMode);
          const cp = findCustomerCustomPrice(item, nextPrices);
          const isCustomerCustom = Boolean(cp && (cp.custom_price > 0 || (cp.custom_delivery_price != null && cp.custom_delivery_price > 0)));
          return {
            ...item,
            rate: newRate,
            customer_custom_price: cp?.custom_price,
            min_rate: isCustomerCustom ? 0 : item.min_rate,
            amount: calcLineAmount(item.quantity, newRate),
          };
        });

        set({ customer: id, customerName: name, customerType: type, customerCustomPrices: nextPrices, items });
      },

      setFulfillmentType: (type, pricingMode) => {
        const prev = get();
        if (prev.fulfillmentType === type) return;

        const items = prev.items.map((item) => {
          const newRate = resolveItemRate(item, prev.customerCustomPrices, prev.customerType, type, pricingMode);
          const cp = findCustomerCustomPrice(item, prev.customerCustomPrices);
          const isCustomerCustom = Boolean(cp && (cp.custom_price > 0 || (cp.custom_delivery_price != null && cp.custom_delivery_price > 0)));
          return {
            ...item,
            rate: newRate,
            customer_custom_price: cp?.custom_price,
            min_rate: isCustomerCustom ? 0 : item.min_rate,
            amount: calcLineAmount(item.quantity, newRate),
          };
        });

        set({ fulfillmentType: type, items });
      },

      setDeliveryInfo: (info) => {
        set((state) => ({
          deliveryInfo: { ...state.deliveryInfo, ...info },
        }));
      },
      setDiscount: (d) => set({ discount: d }),
      setTax: (t) => set({ tax: t }),
      setNotes: (n) => set({ notes: n }),
      setEditingInvoiceDate: (d) => set({ editingInvoiceDate: d }),

      addPayment: (payment) => set({ payments: [...get().payments, payment] }),
      removePayment: (index) => set({ payments: get().payments.filter((_, i) => i !== index) }),
      updatePayment: (index, payment) => {
        const payments = [...get().payments];
        payments[index] = { ...payments[index], ...payment };
        set({ payments });
      },

      clearCart: () => set({ ...emptyActive }),


      holdCart: (opts) => {
        const s = get();
        if (!s.items.length) {
          return { ok: false, error: 'Cart is empty — nothing to hold' };
        }
        if (s.holds.length >= MAX_HELD_CARTS) {
          return {
            ok: false,
            error: `Maximum ${MAX_HELD_CARTS} held carts. Resume or discard one first.`,
          };
        }
        const snap = snapshotFromState(s, opts?.customerMobile);
        set({ holds: [...s.holds, snap], ...emptyActive });
        return { ok: true, id: snap.id };
      },

      resumeHold: (id, opts) => {
        const s = get();
        const idx = s.holds.findIndex((h) => h.id === id);
        if (idx < 0) return { ok: false, error: 'Held cart not found' };

        let nextHolds = [...s.holds];
        const target = nextHolds[idx];

        // If active cart has lines, park it first when there is room.
        if (s.items.length > 0) {
          if (nextHolds.length >= MAX_HELD_CARTS) {
            return {
              ok: false,
              error: `Active cart has items and ${MAX_HELD_CARTS} holds are full. Complete, clear, or discard a hold first.`,
            };
          }
          const parked = snapshotFromState(s, opts?.customerMobile);
          nextHolds = [...nextHolds, parked];
        }

        nextHolds = nextHolds.filter((h) => h.id !== id);
        set({
          holds: nextHolds,
          items: target.items.map((i) => ({ ...i })),
          customer: target.customer,
          customerName: target.customerName,
          customerType: target.customerType || 'Retailer',
          customerCustomPrices: target.customerCustomPrices ? [...target.customerCustomPrices] : [],
          fulfillmentType: target.fulfillmentType || 'Pickup',
          deliveryInfo: target.deliveryInfo ? { ...target.deliveryInfo } : { address: '', postal_code: '', recipient_name: '', phone: '', notes: '' },
          discount: target.discount,
          tax: target.tax,
          notes: target.notes,
          editingInvoiceDate: target.editingInvoiceDate,
          payments: target.payments.map((p) => ({ ...p })),
        });
        return { ok: true };
      },

      discardHold: (id) => {
        set({ holds: get().holds.filter((h) => h.id !== id) });
      },

      getSubtotal: () => roundMoney(get().items.reduce((s, i) => s + i.amount, 0)),
      getItemTotal: () => roundMoney(get().items.filter(i => i.item_type === 'Product').reduce((s, i) => s + i.amount, 0)),
      getServiceTotal: () => roundMoney(get().items.filter(i => i.item_type === 'Service').reduce((s, i) => s + i.amount, 0)),
      getDiscountAmount: () => roundMoney(get().discount),
      getTaxAmount: () => roundMoney(get().tax),
      getNetTotal: () => {
        const s = get().getSubtotal();
        return roundMoney(s - get().discount + get().tax);
      },
      getPaidAmount: () => roundMoney(get().payments.reduce((s, p) => s + (p.amount || 0), 0)),
      getBalance: () => roundMoney(get().getNetTotal() - get().getPaidAmount()),
      getTotalItems: () => get().items.reduce((s, i) => s + i.quantity, 0),
    }),
    {
      name: 'pos-cart-storage',
      storage: createJSONStorage(() => sessionStorage),
      merge: (persisted, current) => {
        const p = (persisted || {}) as Partial<CartStore>;
        return {
          ...current,
          ...p,
          holds: Array.isArray(p.holds) ? p.holds : [],
          items: Array.isArray(p.items) ? p.items : [],
          payments: Array.isArray(p.payments) ? p.payments : [],
        };
      },
    }
  )
);
